import React from "react";
import { useSelector } from "react-redux";
import { Container } from "react-bootstrap";
import {
  calculateLoanInstallment,
  LOAN_ADMIN_RATE,
  LOAN_MIN_AMOUNT,
  LOAN_TENORS,
} from "utils/loanInstallment";
import "./admin-loan-calculator.css";

const ALLOWED_ROLES = ["super admin", "admin", "payroll"];
const NOMINAL_PRESETS = [500000, 1000000, 2500000, 5000000, 10000000];

function money(value) {
  return new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    maximumFractionDigits: 0,
  }).format(Number(value || 0));
}

export default function AdminLoanCalculator() {
  const auth = useSelector((state) => state.authReducer);
  const [nominal, setNominal] = React.useState(500000);
  const [tenor, setTenor] = React.useState(2);
  const calculation = calculateLoanInstallment(nominal, tenor);
  const validNominal = calculation.principal >= LOAN_MIN_AMOUNT;
  const validTenor = calculation.months >= 1;
  const validCalculation = validNominal && validTenor;

  function updateNominal(value) {
    const digits = String(value || "").replace(/\D/g, "");
    setNominal(digits ? Number(digits) : 0);
  }

  function updateTenor(value) {
    const digits = String(value || "").replace(/\D/g, "");
    setTenor(digits ? Number(digits) : 0);
  }

  if (auth.role && !ALLOWED_ROLES.includes(auth.role)) {
    return (
      <Container fluid className="admin-loan-calculator-page">
        <div className="admin-calculator-access">
          <i className="fas fa-lock" />
          <h3>Akses tidak tersedia</h3>
          <p>Menu ini hanya dapat dibuka oleh admin, super admin, dan payroll.</p>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="admin-loan-calculator-page">
      <header className="admin-calculator-header">
        <div>
          <span>Alat bantu pengajuan</span>
          <h1>Kalkulator Angsuran</h1>
          <p>Simulasikan nominal pencairan dan angsuran sebelum diproses.</p>
        </div>
        <div className="admin-calculator-rate">
          <i className="fas fa-percentage" />
          <div>
            <span>Administrasi</span>
            <strong>{LOAN_ADMIN_RATE * 100}%</strong>
          </div>
        </div>
      </header>

      <div className="admin-calculator-layout">
        <section className="admin-calculator-form">
          <div className="admin-calculator-section-title">
            <i className="fas fa-sliders-h" />
            <div>
              <strong>Parameter pinjaman</strong>
              <span>Masukkan nominal dan pilih tenor</span>
            </div>
          </div>

          <label htmlFor="admin-calculator-nominal">Nominal pencairan</label>
          <div className={`admin-calculator-input ${validNominal ? "" : "invalid"}`}>
            <span>Rp</span>
            <input
              id="admin-calculator-nominal"
              type="text"
              inputMode="numeric"
              value={nominal ? nominal.toLocaleString("id-ID") : ""}
              placeholder="500.000"
              onChange={(event) => updateNominal(event.target.value)}
            />
          </div>
          <small className={validNominal ? "" : "invalid"}>
            Nominal pinjaman minimal Rp500.000 tanpa batas maksimum.
          </small>

          <div className="admin-calculator-presets" aria-label="Pilihan nominal cepat">
            {NOMINAL_PRESETS.map((value) => (
              <button
                key={value}
                type="button"
                className={nominal === value ? "active" : ""}
                onClick={() => setNominal(value)}
              >
                {value === 500000 ? "500 rb" : `${value / 1000000} jt`}
              </button>
            ))}
          </div>

          <label>Tenor pinjaman</label>
          <div className="admin-calculator-tenors" role="group" aria-label="Pilihan tenor">
            {LOAN_TENORS.map((value) => (
              <button
                key={value}
                type="button"
                className={tenor === value ? "active" : ""}
                onClick={() => setTenor(value)}
              >
                <strong>{value}</strong>
                <span>bulan</span>
              </button>
            ))}
          </div>
          <div className="admin-calculator-custom-tenor">
            <label htmlFor="admin-calculator-tenor">Tenor custom</label>
            <div className={validTenor ? "" : "invalid"}>
              <input
                id="admin-calculator-tenor"
                type="text"
                inputMode="numeric"
                value={tenor || ""}
                placeholder="Contoh: 12"
                onChange={(event) => updateTenor(event.target.value)}
              />
              <span>bulan</span>
            </div>
            <small className={validTenor ? "" : "invalid"}>
              Tenor minimal 1 bulan dan tidak dibatasi pilihan di atas.
            </small>
          </div>
        </section>

        <section className="admin-calculator-result">
          <div className="admin-calculator-result-main">
            <span>Estimasi angsuran per bulan</span>
            <strong>{validCalculation ? money(calculation.monthlyInstallment) : "-"}</strong>
            <small>{calculation.months} kali pembayaran</small>
          </div>

          <div className="admin-calculator-breakdown">
            <div>
              <span>Pokok per bulan</span>
              <strong>{validCalculation ? money(calculation.monthlyPrincipal) : "-"}</strong>
            </div>
            <div>
              <span>Administrasi per bulan</span>
              <strong>{validCalculation ? money(calculation.monthlyAdministration) : "-"}</strong>
            </div>
            <div>
              <span>Total administrasi 8%</span>
              <strong>{validCalculation ? money(calculation.totalAdministration) : "-"}</strong>
            </div>
            <div className="total">
              <span>Total pembayaran</span>
              <strong>{validCalculation ? money(calculation.totalPayment) : "-"}</strong>
            </div>
          </div>

          <div className={`admin-calculator-validation ${validCalculation ? "valid" : "invalid"}`}>
            <i className={validCalculation ? "fas fa-check-circle" : "fas fa-exclamation-circle"} />
            <span>
              {validCalculation
                ? "Nominal siap digunakan untuk simulasi pengajuan."
                : !validNominal
                  ? "Nominal pinjaman minimal Rp500.000."
                  : "Tenor pinjaman minimal 1 bulan."}
            </span>
          </div>

          <p className="admin-calculator-note">
            <i className="fas fa-info-circle" /> Angka merupakan simulasi dengan
            biaya administrasi 8% untuk seluruh kategori karyawan.
          </p>
        </section>
      </div>
    </Container>
  );
}
